import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Approval } from "@zworks/console-contracts";
import { Link } from "react-router";
import { ApprovalPanel, DigestChip, StatusBadge } from "@/components/bespoke";
import { ConflictState } from "@/components/states";
import { Card } from "@/components/ui/card";
import { ApiError, decideApproval } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

type DecideChoice = "approved" | "declined";

/**
 * The decision controls for one approval. The shared ApprovalPanel carries the digest and
 * revision the caller reviewed; its buttons are absent, never disabled, when the caller cannot
 * decide, so the author of the change never decides their own.
 */
export function DecisionControls({ projectId, entry }: { projectId: string; entry: Approval }) {
  const client = useQueryClient();

  const decide = useMutation({
    mutationFn: (body: { decision: DecideChoice; reason?: string }) =>
      decideApproval(projectId, entry.id, {
        decision: body.decision,
        actionDigest: entry.actionDigest,
        revision: entry.revision,
        reason: body.reason,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["projects", projectId, "approvals"] });
    },
  });

  const conflict = decide.error instanceof ApiError && decide.error.status === 409;
  if (conflict) {
    return (
      <ConflictState
        title="The action changed while you were reviewing"
        description={
          decide.error instanceof ApiError
            ? (decide.error.problem?.detail ??
              "The digest or revision has moved. Nothing was recorded.")
            : "The digest or revision has moved. Nothing was recorded."
        }
        onReload={() => {
          decide.reset();
          void client.invalidateQueries({ queryKey: ["projects", projectId, "approvals"] });
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <ApprovalPanel
        title={`Decision · rev ${entry.revision}`}
        actionDigest={entry.actionDigest}
        revision={entry.revision}
        whatChanged={
          <dl className="grid min-w-0 grid-cols-[minmax(5.5rem,auto)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
            <dt className="text-muted-foreground">Requested by</dt>
            <dd>{entry.requestedBy}</dd>
            <dt className="text-muted-foreground">Requested</dt>
            <dd className="tabular-nums">{formatDateTime(entry.requestedAt)}</dd>
            {entry.expiresAt ? (
              <>
                <dt className="text-muted-foreground">Expires</dt>
                <dd className="tabular-nums">{formatDateTime(entry.expiresAt)}</dd>
              </>
            ) : null}
            {entry.runId ? (
              <>
                <dt className="text-muted-foreground">Run</dt>
                <dd>
                  <Link
                    to={`/runs/${entry.runId}`}
                    className="font-mono text-accent-text underline-offset-4 hover:underline"
                  >
                    {entry.runId.slice(0, 8)}
                  </Link>
                </dd>
              </>
            ) : null}
          </dl>
        }
        isAuthor={!entry.canDecide}
        requesterName={entry.requestedBy}
        isPending={decide.isPending}
        onApprove={(reason) => decide.mutate({ decision: "approved", reason })}
        onReject={(reason) => decide.mutate({ decision: "declined", reason })}
      />
      {decide.isError && !conflict ? (
        <p role="alert" className="text-sm text-danger">
          {decide.error instanceof ApiError
            ? (decide.error.problem?.detail ?? decide.error.message)
            : "The request could not be completed."}
        </p>
      ) : null}
    </div>
  );
}
/**
 * One approval rendered as a working thread, in the manner of the Claude desktop permission
 * view: the agent message is quoted context, the permission card names the exact action, and
 * the four options plus the feedback loop sit together. The rationale is labelled unverified
 * and is never part of what is approved.
 */
export function ApprovalThread({ projectId, entry }: { projectId: string; entry: Approval }) {
  const settled = entry.status !== "pending";

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-[var(--radius-card)] border p-3">
        <p className="mb-1 text-xs font-medium text-muted-foreground">Agent · Unverified</p>
        <p className="text-sm leading-6">
          A decision is needed on <span className="font-medium">{entry.title}</span>. The card below
          names the exact action and revision you would approve.
        </p>
      </div>

      <Card className="flex flex-col gap-2 border-warning p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-medium">Permission request · {entry.actionType}</h2>
          <StatusBadge kind="approval" status={entry.status} />
        </div>
        <p className="text-[0.8125rem] text-muted-foreground">
          Read from the verified record, not from the agent&apos;s summary.
        </p>
        <dl className="grid min-w-0 grid-cols-[minmax(5.5rem,auto)_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm sm:gap-x-4">
          <dt className="text-muted-foreground">Digest</dt>
          <dd>
            <DigestChip value={entry.actionDigest} label="action digest" />
          </dd>
          <dt className="text-muted-foreground">Revision</dt>
          <dd className="tabular-nums">Revision {entry.revision}</dd>
          <dt className="text-muted-foreground">Requested by</dt>
          <dd>{entry.requestedBy}</dd>
          <dt className="text-muted-foreground">Requested</dt>
          <dd className="tabular-nums">{formatDateTime(entry.requestedAt)}</dd>
          {entry.expiresAt ? (
            <>
              <dt className="text-muted-foreground">Expires</dt>
              <dd className="tabular-nums">{formatDateTime(entry.expiresAt)}</dd>
            </>
          ) : null}
          {entry.runId ? (
            <>
              <dt className="text-muted-foreground">Run</dt>
              <dd>
                <Link
                  to={`/runs/${entry.runId}`}
                  className="font-mono text-[0.8125rem] text-accent-text underline-offset-4 hover:underline"
                >
                  {entry.runId.slice(0, 8)}
                </Link>
              </dd>
            </>
          ) : null}
          {entry.decidedBy ? (
            <>
              <dt className="text-muted-foreground">Decided by</dt>
              <dd>{entry.decidedBy}</dd>
            </>
          ) : null}
          {entry.reason ? (
            <>
              <dt className="text-muted-foreground">Reason</dt>
              <dd>{entry.reason}</dd>
            </>
          ) : null}
        </dl>
      </Card>

      <Card className="flex flex-col gap-2 p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-medium">Agent&apos;s rationale</h2>
          <StatusBadge kind="evidence" status="unverified" />
        </div>
        <p className="text-[0.8125rem] text-muted-foreground">
          Quoted context only. It is not part of what you are approving.
        </p>
        <p className="text-sm">No rationale was recorded with this request.</p>
      </Card>

      <Card className="flex flex-col gap-2 p-4 sm:p-5">
        {entry.canDecide && !settled ? (
          <DecisionControls key={entry.id} projectId={projectId} entry={entry} />
        ) : settled ? (
          <p className="text-sm text-muted-foreground">
            This approval was {entry.status}
            {entry.decidedBy ? ` by ${entry.decidedBy}` : ""}. Nothing more to decide.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            You cannot decide this approval. The author of a change never decides their own, and
            eligibility comes from the server.
          </p>
        )}
      </Card>
    </div>
  );
}
